import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    PlusIcon,
    MegaphoneIcon,
    ExclamationTriangleIcon,
    ClockIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { listAnnouncements } from "../api/messagingApi";
import {
    getAnnouncementEmailCount,
    getAnnouncementId,
    getAnnouncementPushCount,
    getAnnouncementType,
} from "../announcement-utils";
import type {
    AnnouncementsQueryParams,
    AnnouncementStatus,
} from "../types";

const STATUS_FILTERS: { value: AnnouncementStatus | ""; label: string }[] = [
    { value: "", label: "All Statuses" },
    { value: "SENT", label: "Sent" },
    { value: "DRAFT", label: "Draft" },
    { value: "SCHEDULED", label: "Scheduled" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "FAILED", label: "Failed" },
];

const statusColors: Record<string, string> = {
    DRAFT: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
    SCHEDULED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    SENT: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    CANCELLED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

const typeColors: Record<string, string> = {
    ROAD_CLOSURE: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    SYSTEM_MAINTENANCE: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    SAFETY_WARNING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    PROMOTION: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    GENERAL: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
    EMERGENCY: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

const fmtNumber = (n: number | null | undefined) =>
    n == null ? "—" : new Intl.NumberFormat("en-NG").format(n);

const fmtDate = (d: string | null | undefined) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

const fmtLabel = (value: string | null | undefined) => {
    if (!value) return "—";
    return value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const AnnouncementsRoute = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const locationFeedback = (location.state as
        | { feedback?: { type: "success" | "error"; text: string } }
        | undefined)?.feedback;
    const [filters, setFilters] = useState<AnnouncementsQueryParams>({
        page: 0,
        size: 20,
    });

    const { data, isLoading, error } = useQuery({
        queryKey: ["announcements", filters],
        queryFn: () => listAnnouncements(filters),
    });

    const announcements = data?.data?.announcements ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const canManageMessaging = hasPermission(SECTION_PERMISSIONS.MESSAGING_MANAGE[0]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2">
                    {STATUS_FILTERS.map((statusFilter) => (
                        <button
                            key={statusFilter.value}
                            onClick={() =>
                                setFilters((previousFilters) => ({
                                    ...previousFilters,
                                    status: statusFilter.value
                                        ? (statusFilter.value as AnnouncementStatus)
                                        : undefined,
                                    page: 0,
                                }))
                            }
                            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                                (filters.status ?? "") === statusFilter.value
                                    ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                            }`}
                        >
                            {statusFilter.label}
                        </button>
                    ))}
                </div>
                {canManageMessaging ? (
                    <div className="flex gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                navigate("/notifications/announcements/new?mode=draft")
                            }
                        >
                            <DocumentTextIcon className="mr-1.5 h-4 w-4" /> Draft
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                navigate("/notifications/announcements/new?mode=schedule")
                            }
                        >
                            <ClockIcon className="mr-1.5 h-4 w-4" /> Schedule
                        </Button>
                        <Button
                            size="sm"
                            onClick={() =>
                                navigate("/notifications/announcements/new?mode=send")
                            }
                        >
                            <PlusIcon className="mr-1.5 h-4 w-4" /> Send Now
                        </Button>
                    </div>
                ) : null}
            </div>

            {locationFeedback ? (
                <div
                    className={`rounded-xl px-4 py-3 text-sm ${
                        locationFeedback.type === "success"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    {locationFeedback.text}
                </div>
            ) : null}

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                </div>
            ) : null}

            {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        Failed to load announcements
                    </p>
                </div>
            ) : null}

            {!isLoading && !error ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                    {announcements.length === 0 ? (
                        <div className="py-16 text-center">
                            <MegaphoneIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                                No announcements found
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Title
                                        </th>
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Audience
                                        </th>
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Delivered
                                        </th>
                                        <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
                                            Created
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {announcements.map((announcement, index) => {
                                        const announcementId = getAnnouncementId(announcement);
                                        const announcementType =
                                            getAnnouncementType(announcement) ?? "GENERAL";
                                        const pushCount =
                                            getAnnouncementPushCount(announcement) ?? 0;
                                        const emailCount =
                                            getAnnouncementEmailCount(announcement) ?? 0;
                                        const announcementStatus =
                                            announcement.status ?? "DRAFT";

                                        return (
                                            <tr
                                                key={
                                                    announcementId ??
                                                    `${announcement.title || "announcement"}-${index}`
                                                }
                                                className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                            >
                                                <td className="px-4 py-3">
                                                    {announcementId ? (
                                                        <Link
                                                            to={`/notifications/announcements/${announcementId}`}
                                                            className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                                        >
                                                            {announcement.title || "Untitled Announcement"}
                                                        </Link>
                                                    ) : (
                                                        <div className="space-y-1">
                                                            <span className="font-medium text-slate-900 dark:text-white">
                                                                {announcement.title || "Untitled Announcement"}
                                                            </span>
                                                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                                                Details unavailable for this announcement
                                                            </p>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                                            typeColors[announcementType] ??
                                                            typeColors.GENERAL
                                                        }`}
                                                    >
                                                        {fmtLabel(announcementType)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                                    {fmtLabel(announcement.targetAudience)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                                            statusColors[announcementStatus] ??
                                                            statusColors.DRAFT
                                                        }`}
                                                    >
                                                        {fmtLabel(announcementStatus)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                                                    <div className="flex gap-3 text-xs">
                                                        {pushCount > 0 ? (
                                                            <span>{fmtNumber(pushCount)} push</span>
                                                        ) : null}
                                                        {emailCount > 0 ? (
                                                            <span>{fmtNumber(emailCount)} email</span>
                                                        ) : null}
                                                        {!pushCount && !emailCount && "—"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                                    {fmtDate(announcement.createdAt)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 ? (
                        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 dark:border-slate-700">
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Page {(filters.page ?? 0) + 1} of {totalPages}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={(filters.page ?? 0) === 0}
                                    onClick={() =>
                                        setFilters((previousFilters) => ({
                                            ...previousFilters,
                                            page: (previousFilters.page ?? 0) - 1,
                                        }))
                                    }
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={(filters.page ?? 0) >= totalPages - 1}
                                    onClick={() =>
                                        setFilters((previousFilters) => ({
                                            ...previousFilters,
                                            page: (previousFilters.page ?? 0) + 1,
                                        }))
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
